import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { request } from '@/shared/api/request'
import { Modal } from '@/shared/components/Modal'
import { countryFlag } from '@/shared/lib/country'

// Only the fields the popup needs; extra keys are stripped by zod.
const countriesSchema = z.object({
  data: z.array(
    z.object({
      code: z.string(),
      status: z.number(),
      release_date: z.string().nullable(),
    }),
  ),
})

/** CountryStatus wire values (backend Common.Enums.CountryStatus). */
const STATUS_PENDING = 10
const STATUS_RELEASED = 20

/** yyyy-MM-dd parsed as local time (UTC parsing would shift the day west of Greenwich). */
function formatReleaseDate(iso: string, language: string): string {
  const [year, month, day] = iso.split('-').map(Number)
  const date = new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1)
  return date.toLocaleDateString(language, { year: 'numeric', month: 'long', day: 'numeric' })
}

/**
 * Post-change country popup: a playful card with the country flag telling the
 * user whether Eleva is officially released there (released / pending with
 * the release date / not targeted yet). Shown after every successful country
 * change — settings and the first-login gate.
 */
export function CountryStatusModal({ code, onClose }: { code: string | null; onClose: () => void }) {
  const { t, i18n } = useTranslation('settings')

  // The status comes from the countries reference endpoint, cached for the session.
  const countries = useQuery({
    queryKey: ['countries'],
    queryFn: () => request({ method: 'GET', url: '/api/eleva/countries' }, countriesSchema),
    staleTime: Infinity,
    enabled: code !== null,
  })

  if (!code || !countries.isSuccess) return null
  const country = countries.data.data.find((c) => c.code.toUpperCase() === code.toUpperCase())
  if (!country) return null

  const names = new Intl.DisplayNames([i18n.language], { type: 'region' })
  const countryName = names.of(code.toUpperCase()) ?? code.toUpperCase()

  let title: string
  let body: string
  if (country.status >= STATUS_RELEASED) {
    title = t('countryStatus.releasedTitle')
    body = t('countryStatus.releasedBody', { country: countryName })
  } else if (country.status >= STATUS_PENDING) {
    title = t('countryStatus.pendingTitle')
    body = country.release_date
      ? t('countryStatus.pendingBody', {
          country: countryName,
          date: formatReleaseDate(country.release_date, i18n.language),
        })
      : t('countryStatus.pendingBodyNoDate', { country: countryName })
  } else {
    title = t('countryStatus.unreleasedTitle')
    body = t('countryStatus.unreleasedBody', { country: countryName })
  }

  return (
    <Modal open onClose={onClose} size="sm">
      <div className="flex flex-col items-center gap-4 text-center">
        <span className="bg-surface flex size-18 items-center justify-center rounded-full text-5xl">
          {countryFlag(code)}
        </span>
        <p className="text-fg text-lg font-bold">{title}</p>
        <p className="text-fg text-sm leading-relaxed">{body}</p>
        <button
          type="button"
          onClick={onClose}
          className="bg-primary hover:bg-primary-hover w-full rounded-xl py-2.5 text-sm font-semibold text-white transition"
        >
          {t('countryStatus.gotIt')}
        </button>
      </div>
    </Modal>
  )
}
